import { ApiResponseError } from '@api/apiUtils/ApiResponseError';
import { useCustomMutation } from '@utils/hooks/useCustomMutation';

import type { MutationKey, UseMutationOptions } from '@tanstack/react-query';
import type {
  SubmitFormMutationFunction,
  UseSubmitFormMutationResult,
} from '@utils/hooks/mutation.types';

/**
 * Хук `useSubmitFormMutation` предоставляет возможность создания мутаций для отправки данных из формы
 * с использованием предоставленной функции мутации и ключа мутации.
 *
 * @template TVariables Тип переменных, передаваемых в функцию мутации. По умолчанию `unknown`.
 * @template TData Тип данных, возвращаемых при успешной мутации. По умолчанию `unknown`.
 * @template TContext Тип дополнительных данных (контекста), доступного в пользовательских обработчиках мутации. По умолчанию `unknown`.
 * @template TError Тип ошибки, возвращаемой в случае сбоя мутации. По умолчанию `ApiResponseError`.
 *
 * @param {MutationKey} mutationKey Уникальный ключ мутации, используемый для идентификации конкретной функции мутации.
 * @param {SubmitFormMutationFunction<TVariables, TData>} mutationFunction Функция, выполняющая операцию мутации.
 * @param {Omit<UseMutationOptions<TData, ApiResponseError, Partial<TVariables>>, 'mutationKey' | 'mutationFn'>} [options] Опции для настройки поведения мутации, за исключением `mutationKey` и `mutationFn`.
 *
 * @returns {UseSubmitFormMutationResult<TVariables, TData, TContext, TError>} Объект, содержащий метод `submit` для выполнения мутации
 * и дополнительные свойства результата мутации (например, статус).
 */
export const useSubmitFormMutation = <
  TVariables = unknown, // переменные для функции
  TData = unknown, // то что должно быть возвращено
  TContext = unknown,
  TError = ApiResponseError,
>(
  mutationKey: MutationKey,
  mutationFunction: SubmitFormMutationFunction<TVariables, TData>,
  options?: Omit<
    UseMutationOptions<TData, ApiResponseError, Partial<TVariables>>,
    'mutationKey' | 'mutationFn'
  >,
): UseSubmitFormMutationResult<TVariables, TData, TContext, TError> => {
  const { mutate, ...rest } = useCustomMutation<TVariables, TData>(
    mutationKey,
    mutationFunction,
    options,
  );
  const submit = async (values: TVariables): Promise<TData> => {
    return new Promise(resolve => {
      mutate(values, {
        onSuccess: data => {
          resolve(data);
        },
        onError: () => {
          resolve(undefined as unknown as TData);
        },
      });
    });
  };
  return { submit, ...rest } as UseSubmitFormMutationResult<
    TVariables,
    TData,
    TContext,
    TError
  >;
};
